import React from 'react';
import { Box, IconButton, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  ACTION_COLORS, ACTION_LABELS, BORDER, DARK, EVAL_COLORS, EVAL_LABELS, MODE_COLORS, MODE_LABELS, MUTED, PANEL_SHADOW, PRIMARY, PRIMARY_SOFT, SOFT, TRACK
} from './rulesConstants';
import { EvalStatus, RuleMode } from './rulesTypes';

/** Шапка таблиц окна - как в отчётах: светлая, жирная, с утолщённой нижней линией */
export const HEAD_ROW_SX = {
  '& th': { bgcolor: SOFT, fontWeight: 700, fontSize: '0.82rem', color: '#475569', borderBottom: `2px solid ${BORDER}`, py: 1.2, whiteSpace: 'nowrap' }
} as const;

/** Тело таблицы: тонкие разделители и лёгкий hover */
export const TABLE_SX = {
  '& td': { borderColor: '#f1f5f9', fontSize: '0.84rem', color: DARK },
  '& tbody tr:last-of-type td': { borderBottom: 'none' },
  '& tbody tr:hover td': { bgcolor: TRACK }
} as const;

/** Белый блок с тонкой рамкой - основа всех блоков окна */
export const Panel: React.FC<{ children?: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => (
  <Box
    sx={[
      { bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: PANEL_SHADOW, overflow: 'hidden' },
      ...(Array.isArray(sx) ? sx : [sx])
    ]}
  >
    {children}
  </Box>
);

/** Пустое состояние списков: заголовок, пояснение, действие */
export const EmptyState: React.FC<{ title: string; text?: string; action?: React.ReactNode }> = ({ title, text, action }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.5, py: 5, px: 3 }}>
    <Typography sx={{ fontWeight: 700, color: DARK, fontSize: 14.5 }}>{title}</Typography>
    {text && <Typography sx={{ fontSize: 13, color: MUTED, maxWidth: 440 }}>{text}</Typography>}
    {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
  </Box>
);

/** Заголовок блока: название, пояснение и (справа) произвольное действие */
export const PanelHeader: React.FC<{ title: string; subtitle?: string; right?: React.ReactNode }> = ({ title, subtitle, right }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, bgcolor: SOFT, borderBottom: `1px solid ${BORDER}` }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, color: DARK, fontSize: 15, lineHeight: 1.35 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.25 }}>{subtitle}</Typography>}
    </Box>
    {right}
  </Box>
);

export interface SegOption<T extends string> { value: T; label: string; color?: string; bg?: string; hint?: string }

/** Групповой переключатель (как ToggleButtonGroup): выбранный вариант подсвечен, цвет задаётся вариантом */
export function Segmented<T extends string>({ value, onChange, options, disabled, small }: {
  value: T;
  onChange: (v: T) => void;
  options: SegOption<T>[];
  disabled?: boolean;
  small?: boolean;
}) {
  return (
    <Box
      role="radiogroup"
      sx={{
        display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden', bgcolor: '#fff', maxWidth: '100%',
        opacity: disabled ? 0.55 : 1, pointerEvents: disabled ? 'none' : 'auto'
      }}
    >
      {options.map((o, i) => {
        const sel = o.value === value;
        return (
          <Tooltip key={o.value} title={o.hint || ''} placement="top" arrow disableInteractive>
            <Box
              component="button" type="button" role="radio" aria-checked={sel} tabIndex={disabled ? -1 : 0}
              onClick={() => { if (!sel) onChange(o.value); }}
              sx={{
                px: small ? 1.25 : 1.6, py: small ? 0.55 : 0.8, m: 0, border: 'none', borderLeft: i ? '1px solid #cbd5e1' : 'none', borderRadius: 0,
                cursor: sel ? 'default' : 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', fontSize: small ? 12.5 : 13, fontWeight: 600,
                bgcolor: sel ? (o.bg || PRIMARY_SOFT) : '#fff', color: sel ? (o.color || PRIMARY) : MUTED,
                transition: 'background-color .15s, color .15s',
                '&:hover': sel ? undefined : { bgcolor: SOFT, color: DARK },
                '&:focus-visible': { outline: `2px solid ${PRIMARY}`, outlineOffset: -2 }
              }}
            >
              {o.label}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

/** Варианты режима для Segmented (подписи можно переопределить: у исключений off = «Не применять») */
export const modeOptions = (
  modes: RuleMode[], labels: Record<RuleMode, string>, hints?: Record<RuleMode, string>
): SegOption<RuleMode>[] => modes.map(m => ({ value: m, label: labels[m], color: MODE_COLORS[m].color, bg: MODE_COLORS[m].bg, hint: hints?.[m] }));

/** Статус-метка: тонированный фон и цвет текста, без значков */
const Pill: React.FC<{ label: string; color: string; bg: string }> = ({ label, color, bg }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: '6px', bgcolor: bg, color,
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap'
    }}
  >
    {label}
  </Box>
);

export const ModeChip: React.FC<{ mode: RuleMode; label?: string }> = ({ mode, label }) => (
  <Pill label={label || MODE_LABELS[mode]} color={MODE_COLORS[mode].color} bg={MODE_COLORS[mode].bg} />
);

export const EvalChip: React.FC<{ status: EvalStatus }> = ({ status }) => (
  <Pill label={EVAL_LABELS[status]} color={EVAL_COLORS[status].color} bg={EVAL_COLORS[status].bg} />
);

export const ActionChip: React.FC<{ action: 'create' | 'update' | 'delete' }> = ({ action }) => (
  <Pill label={ACTION_LABELS[action]} color={ACTION_COLORS[action].color} bg={ACTION_COLORS[action].bg} />
);

/** Оформление вторичных диалогов окна (форма исключения, подтверждение удаления) */
export const DIALOG_SLOT_PROPS = {
  paper: { sx: { borderRadius: '12px', backgroundImage: 'none', overflow: 'hidden' } },
  backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.45)' } }
} as const;

/** Кнопка закрытия в шапках диалогов */
export const CloseButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <IconButton onClick={onClick} aria-label="Закрыть" size="small" sx={{ color: MUTED, '&:hover': { bgcolor: TRACK, color: DARK } }}>
    <CloseRoundedIcon fontSize="small" />
  </IconButton>
);

/** Светлая шапка вторичного диалога: заголовок, пояснение и (необязательно) кнопка закрытия */
export const DialogHeader: React.FC<{ title: string; subtitle?: string; onClose?: () => void }> = ({ title, subtitle, onClose }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2, bgcolor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, color: DARK, fontSize: 17, lineHeight: 1.3 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.25 }}>{subtitle}</Typography>}
    </Box>
    {onClose && <CloseButton onClick={onClose} />}
  </Box>
);

/** Направление/аптека в таблицах: «Аптека №1 → Аптека №2» или просто «Аптека №1»; «Вся сеть» для сетевых записей */
export const ScopeCell: React.FC<{ scopeType: string; from?: string | null; to?: string | null }> = ({ scopeType, from, to }) => {
  if (scopeType === 'network') return <Box component="span" sx={{ color: MUTED }}>Вся сеть</Box>;
  return (
    <Box component="span" sx={{ fontWeight: 600, color: DARK }}>
      {from || '-'}
      {scopeType === 'direction' && <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400 }}> → </Box>}
      {scopeType === 'direction' && (to || '-')}
    </Box>
  );
};
